import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, OutlinedInput,
  Checkbox, ListItemText, Alert, CircularProgress, Stack, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Send as SendIcon, ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { telegramAPI, accountsAPI } from '../services/api';

const SEVERITY_OPTS = [
  { v: 'all',     label: 'Mọi mức (info+warning+error)' },
  { v: 'info',    label: 'Info trở lên' },
  { v: 'warning', label: 'Warning trở lên (mặc định)' },
  { v: 'error',   label: 'Chỉ Error' },
];

const SOURCE_OPTS = [
  { v: 'psychology', label: 'Đánh giá cảm xúc (TPM)' },
  { v: 'sync',       label: 'Sync Checker' },
  { v: 'slave',      label: 'Slave EA' },
  { v: 'http',       label: 'HTTP / khác' },
  { v: 'websocket',  label: 'WebSocket / khác' },
];

const emptyForm = () => ({
  id: null,
  name: '',
  bot_token: '',
  chat_id: '',
  enabled: true,
  account_filter: [],
  severity_filter: 'warning',
  source_filter: [],
});

export default function TelegramSettings() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [testing, setTesting] = useState(null);  // id being tested
  const [testMsg, setTestMsg] = useState(null);  // { type, text }

  const { data: list, isLoading } = useQuery('telegram-configs', () => telegramAPI.getAll());
  const { data: acc } = useQuery('accounts', () => accountsAPI.getAll(), { staleTime: 60000 });
  const accounts = acc?.data || [];
  const configs = list?.data?.data || [];

  const closeDialog = () => { setOpen(false); setForm(emptyForm()); };

  const createM = useMutation((d) => telegramAPI.create(d), {
    onSuccess: () => { qc.invalidateQueries('telegram-configs'); closeDialog(); },
  });
  const updateM = useMutation(({ id, d }) => telegramAPI.update(id, d), {
    onSuccess: () => { qc.invalidateQueries('telegram-configs'); closeDialog(); },
  });
  const deleteM = useMutation((id) => telegramAPI.delete(id), {
    onSuccess: () => qc.invalidateQueries('telegram-configs'),
  });

  const handleEdit = (cfg) => {
    setForm({
      id: cfg.id,
      name: cfg.name,
      bot_token: '',  // Empty = keep existing — user must re-type to change
      chat_id: cfg.chat_id,
      enabled: !!cfg.enabled,
      account_filter: Array.isArray(cfg.account_filter) ? cfg.account_filter : [],
      severity_filter: cfg.severity_filter || 'warning',
      source_filter: Array.isArray(cfg.source_filter) ? cfg.source_filter : [],
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.chat_id.trim()) return;
    if (!form.id && !form.bot_token.trim()) return;
    const payload = {
      name: form.name.trim(),
      chat_id: form.chat_id.trim(),
      enabled: form.enabled,
      severity_filter: form.severity_filter,
      account_filter: form.account_filter.length > 0 ? form.account_filter : null,
      source_filter: form.source_filter.length > 0 ? form.source_filter : null,
    };
    // Only send bot_token when user actually entered one (else keep existing)
    if (form.bot_token.trim()) payload.bot_token = form.bot_token.trim();
    if (form.id) updateM.mutate({ id: form.id, d: payload });
    else createM.mutate(payload);
  };

  const handleTest = async (cfg) => {
    setTesting(cfg.id);
    setTestMsg(null);
    try {
      await telegramAPI.testStored(cfg.id);
      setTestMsg({ type: 'success', text: `Đã gửi test → ${cfg.name}` });
    } catch (err) {
      setTestMsg({ type: 'error', text: `${cfg.name}: ${err.response?.data?.error || err.message}` });
    } finally {
      setTesting(null);
    }
  };

  const handleTestForm = async () => {
    if (!form.bot_token.trim() || !form.chat_id.trim()) {
      setTestMsg({ type: 'warning', text: 'Cần nhập bot_token + chat_id để test' });
      return;
    }
    setTesting('form');
    setTestMsg(null);
    try {
      await telegramAPI.testRaw({ bot_token: form.bot_token.trim(), chat_id: form.chat_id.trim() });
      setTestMsg({ type: 'success', text: 'Test thành công! Bot trả về OK.' });
    } catch (err) {
      setTestMsg({ type: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setTesting(null);
    }
  };

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Telegram Bot — kênh nhận thông báo
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mọi cảnh báo từ EA (TPM, Sync Checker, Slave) sẽ được forward sang các kênh đang bật,
            theo bộ lọc tài khoản + mức độ.
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }}
          onClick={() => { setForm(emptyForm()); setOpen(true); }}
        >
          Thêm kênh
        </Button>
      </Box>

      {testMsg && (
        <Alert severity={testMsg.type} sx={{ mt: 1, mb: 1, fontSize: '0.85rem' }}>
          {testMsg.text}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : configs.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
          Chưa có kênh Telegram nào. Bấm <strong>Thêm kênh</strong>, lấy bot_token từ <code>@BotFather</code> và
          chat_id từ <code>@userinfobot</code>.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tên</TableCell>
              <TableCell>Chat ID</TableCell>
              <TableCell>Bot token</TableCell>
              <TableCell>Mức độ</TableCell>
              <TableCell>Tài khoản</TableCell>
              <TableCell>Nguồn</TableCell>
              <TableCell align="center">Bật</TableCell>
              <TableCell align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map(c => (
              <TableRow key={c.id}>
                <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{c.chat_id}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.disabled' }}>
                  {c.bot_token}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={c.severity_filter} sx={{ height: 20, fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>
                  {Array.isArray(c.account_filter) && c.account_filter.length > 0
                    ? `${c.account_filter.length} TK`
                    : <Typography component="span" variant="caption" color="text.disabled">Tất cả</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>
                  {Array.isArray(c.source_filter) && c.source_filter.length > 0
                    ? c.source_filter.join(', ')
                    : <Typography component="span" variant="caption" color="text.disabled">Tất cả</Typography>}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={!!c.enabled}
                    onChange={(e) => updateM.mutate({ id: c.id, d: { enabled: e.target.checked } })}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Gửi test">
                    <span>
                      <IconButton size="small" disabled={testing === c.id} onClick={() => handleTest(c)}>
                        {testing === c.id ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Sửa">
                    <IconButton size="small" onClick={() => handleEdit(c)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Xóa">
                    <IconButton size="small" color="error"
                      onClick={() => { if (window.confirm(`Xóa kênh "${c.name}"?`)) deleteM.mutate(c.id); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Sửa kênh Telegram' : 'Thêm kênh Telegram'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tên hiển thị" fullWidth size="small"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="VD: Admin VIP, Group Trading"
            />
            <TextField
              label={form.id ? 'Bot token (để trống = giữ nguyên)' : 'Bot token (lấy từ @BotFather)'}
              fullWidth size="small"
              value={form.bot_token} onChange={(e) => setForm({ ...form, bot_token: e.target.value })}
              placeholder="123456789:AABBCC..."
              type="password" autoComplete="new-password"
            />
            <TextField
              label="Chat ID (lấy từ @userinfobot hoặc /getUpdates)"
              fullWidth size="small"
              value={form.chat_id} onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
              placeholder="123456789 hoặc -100123456789 (group)"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Lọc theo mức độ</InputLabel>
              <Select
                value={form.severity_filter}
                label="Lọc theo mức độ"
                onChange={(e) => setForm({ ...form, severity_filter: e.target.value })}
              >
                {SEVERITY_OPTS.map(o => (
                  <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Lọc tài khoản (mặc định: tất cả)</InputLabel>
              <Select
                multiple
                value={form.account_filter}
                onChange={(e) => setForm({ ...form, account_filter: e.target.value })}
                input={<OutlinedInput label="Lọc tài khoản (mặc định: tất cả)" />}
                renderValue={(s) => s.length === 0 ? '(tất cả)' : `${s.length} tài khoản`}
              >
                {accounts.map(a => (
                  <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                    <Checkbox checked={form.account_filter.includes(a.mt5_account_id)} size="small" />
                    <ListItemText primary={`${a.mt5_account_id} — ${a.name}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Lọc nguồn alert (mặc định: tất cả)</InputLabel>
              <Select
                multiple
                value={form.source_filter}
                onChange={(e) => setForm({ ...form, source_filter: e.target.value })}
                input={<OutlinedInput label="Lọc nguồn alert (mặc định: tất cả)" />}
                renderValue={(s) => s.length === 0 ? '(tất cả)' : s.join(', ')}
              >
                {SOURCE_OPTS.map(o => (
                  <MenuItem key={o.v} value={o.v}>
                    <Checkbox checked={form.source_filter.includes(o.v)} size="small" />
                    <ListItemText primary={o.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              <Typography variant="body2">Bật kênh này</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestForm}
            disabled={testing === 'form' || !form.bot_token.trim() || !form.chat_id.trim()}
            startIcon={testing === 'form' ? <CircularProgress size={14} /> : <SendIcon />}
          >
            Gửi test
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDialog}>Hủy</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={createM.isLoading || updateM.isLoading}
          >
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
